import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, FileText, Trash2, Copy, ExternalLink, Code2, Loader2, Calendar, AlertTriangle, Radio, ImagePlus, ImageOff } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";
import { useAuth } from "@/lib/auth";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function PulseSiteDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [keywordChoice, setKeywordChoice] = useState("");
  const [copied, setCopied] = useState("");
  const [showEmbed, setShowEmbed] = useState(false);

  const load = async () => {
    if (!user) return;
    try {
      const s = await api.get(`/pulse/sites/${id}`, { headers: authHeaders() });
      setSite(s.data);
      const p = await api.get(`/pulse/sites/${id}/posts`, { headers: authHeaders() });
      setPosts(p.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load site");
    }
  };
  useEffect(() => { load(); }, [user, id]);

  if (loading) return null;
  if (!user) {
    return (
      <main className="max-w-md mx-auto px-5 py-32 text-center">
        <h1 className="fl-display text-3xl font-bold">Sign in to access Pulse</h1>
        <Link to="/login" className="fl-btn mt-6 inline-flex">Sign in</Link>
      </main>
    );
  }

  const generate = async () => {
    setBusy("generate"); setErr("");
    try {
      const body = keywordChoice ? { keyword: keywordChoice } : {};
      const { data } = await api.post(`/pulse/sites/${id}/generate`, body, { headers: authHeaders() });
      setKeywordChoice("");
      // Poll the job — Cloudflare's 100s timeout means we can't keep the
      // generate request open. Backend kicks off async work and returns a job_id.
      const jobId = data.job_id;
      const started = Date.now();
      let lastStage = "writing";
      while (Date.now() - started < 180000) { // 3 min hard cap
        await new Promise(r => setTimeout(r, 3000));
        try {
          const { data: j } = await api.get(`/pulse/jobs/${jobId}`, { headers: authHeaders() });
          if (j.stage && j.stage !== lastStage) { lastStage = j.stage; setBusy(`generate-${j.stage}`); }
          if (j.status === "done") {
            await load();
            if (j.post_slug) window.open(`/p/${j.post_slug}`, "_blank", "noopener");
            return;
          }
          if (j.status === "failed") {
            setErr(j.error || "Generation failed. Try again or pick a different keyword.");
            return;
          }
        } catch (_) { /* keep polling */ }
      }
      setErr("Generation is taking longer than expected. Refresh the page in a minute — your post may already be ready.");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Generation failed. Check your monthly quota or try again.");
    } finally {
      setBusy("");
    }
  };

  const removeSite = async () => {
    if (!window.confirm(`Delete "${site?.name}" and all its posts? This is permanent.`)) return;
    setBusy("delete-site");
    try {
      await api.delete(`/pulse/sites/${id}`, { headers: authHeaders() });
      navigate("/pulse");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
      setBusy("");
    }
  };

  const removePost = async (post_id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/pulse/posts/${post_id}`, { headers: authHeaders() });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  const regenCover = async (post_id) => {
    setBusy(`regen-${post_id}`); setErr("");
    try {
      await api.post(`/pulse/posts/${post_id}/regenerate-cover`, {}, { headers: authHeaders() });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't regenerate cover. Try again in a moment.");
    }
    setBusy("");
  };

  const origin = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
  const embedSnippet = site ? `<div data-fliboe-pulse="${site.embed_id}"></div>\n<script src="${origin}/api/pulse/embed/${site.embed_id}.js" defer></script>` : "";

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setCopied("embed"); setTimeout(() => setCopied(""), 1500);
    } catch (_) { /* clipboard not available */ }
  };

  if (!site) {
    return <main className="max-w-4xl mx-auto px-5 py-20 text-white/50">Loading…</main>;
  }

  return (
    <main className="max-w-5xl mx-auto px-5 py-12" data-testid="pulse-site-detail">
      <Link to="/pulse" className="text-white/50 text-sm inline-flex items-center gap-1 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> All Pulse sites
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-fuchsia-300/80 uppercase tracking-widest font-semibold">{site.niche}</div>
          <h1 className="mt-1 text-4xl sm:text-5xl font-black tracking-tight text-white truncate">{site.name}</h1>
          {site.linked_station_id && (
            <Link
              to={`/station/${site.linked_station_id}`}
              className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-xs font-semibold hover:bg-emerald-500/25 transition-colors"
              data-testid="pulse-linked-station-badge"
            >
              <Radio className="w-3 h-3" /> Linked to your station — open
            </Link>
          )}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(site.keywords || []).map(k => (
              <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">{k}</span>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowEmbed(s => !s)} className="fl-btn ghost" data-testid="pulse-show-embed">
            <Code2 className="w-4 h-4" /> Embed code
          </button>
          <button onClick={removeSite} disabled={busy === "delete-site"} className="fl-btn ghost" data-testid="pulse-delete-site">
            <Trash2 className="w-4 h-4 text-rose-400" /> Delete site
          </button>
        </div>
      </div>

      {/* Generate panel */}
      <section className="fl-card p-5 mb-8" data-testid="pulse-generate-panel">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs uppercase tracking-widest text-white/40 mb-2">Generate next post</label>
            <select
              value={keywordChoice}
              onChange={e => setKeywordChoice(e.target.value)}
              className="fl-input w-full"
              data-testid="pulse-keyword-select"
            >
              <option value="">Auto — rotate through your keywords</option>
              {(site.keywords || []).map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <button onClick={generate} disabled={busy.startsWith("generate")} className="fl-btn" data-testid="pulse-generate-btn">
            {busy.startsWith("generate") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy === "generate" || busy === "generate-writing" ? "Writing post…"
              : busy === "generate-illustrating" ? "Painting cover image…"
              : "Generate post"}
          </button>
        </div>
        <div className="text-xs text-white/40 mt-3">Each post: ~$0.04 of LLM credit. Claude writes ~1300 words + meta tags. Nano Banana paints a 16:9 cover. Takes ~45–90s.</div>
        {err && (
          <div className="mt-3 flex items-start gap-2 text-rose-400 text-sm" data-testid="pulse-generate-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {err}
          </div>
        )}
      </section>

      {/* Embed panel */}
      {showEmbed && (
        <section className="fl-card p-5 mb-8" data-testid="pulse-embed-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-white">Embed on any site</div>
            <button onClick={copyEmbed} className="fl-btn ghost !py-1.5 text-xs" data-testid="pulse-copy-embed">
              <Copy className="w-3.5 h-3.5" /> {copied === "embed" ? "Copied!" : "Copy"}
            </button>
          </div>
          <pre className="bg-black/50 border border-white/10 rounded-lg p-4 text-xs text-emerald-200 overflow-x-auto" data-testid="pulse-embed-code">
{embedSnippet}
          </pre>
          <div className="text-xs text-white/40 mt-3">
            Paste this on your own website <strong className="text-emerald-300">once</strong>. It updates by itself: every new
            auto-generated article appears at the top within minutes, no need to change the code. Posts link back to the full article on Fliboe.
          </div>
        </section>
      )}

      {/* Posts list */}
      <section data-testid="pulse-posts-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="fl-display text-2xl font-bold text-white">Posts <span className="text-white/30 font-normal">({posts.length})</span></h2>
        </div>
        {posts.length === 0 ? (
          <div className="fl-card p-10 text-center" data-testid="pulse-posts-empty">
            <FileText className="w-10 h-10 text-fuchsia-400/60 mx-auto mb-3" />
            <div className="text-white font-semibold">No posts yet</div>
            <div className="text-white/50 text-sm mt-1">Click &quot;Generate post&quot; above to ship your first one.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {posts.map(p => (
              <div key={p.post_id} className="fl-card p-0 overflow-hidden group" data-testid={`pulse-post-card-${p.post_id}`}>
                {p.cover_url ? (
                  <img src={resolveAudio(p.cover_url)} alt="" loading="lazy" className="w-full aspect-[16/9] object-cover bg-black/40" />
                ) : (
                  <div className="w-full aspect-[16/9] flex flex-col items-center justify-center bg-gradient-to-br from-fuchsia-500/10 to-emerald-500/10 border-b border-white/5 gap-2">
                    <ImageOff className="w-7 h-7 text-white/30" />
                    <div className="text-xs text-white/50">No cover image — generation returned empty</div>
                  </div>
                )}
                <div className="p-4">
                  <div className="text-xs text-fuchsia-300/70 uppercase tracking-widest font-semibold mb-1">{p.keyword}</div>
                  <div className="text-white font-bold text-lg leading-tight mb-1.5 line-clamp-2">{p.title}</div>
                  <div className="text-white/55 text-sm line-clamp-2">{p.meta_description}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-xs text-white/40 inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {p.published_at ? new Date(p.published_at).toLocaleDateString() : "Draft"}
                    </div>
                    <div className="flex gap-1">
                      {!p.cover_url && (
                        <button
                          onClick={() => regenCover(p.post_id)}
                          disabled={busy === `regen-${p.post_id}`}
                          className="fl-btn ghost !py-1 !px-2 text-xs"
                          data-testid={`pulse-post-regen-cover-${p.post_id}`}
                          aria-label="Regenerate cover"
                        >
                          {busy === `regen-${p.post_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5 text-fuchsia-400" />}
                          {busy === `regen-${p.post_id}` ? "..." : "Cover"}
                        </button>
                      )}
                      <Link to={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className="fl-btn ghost !py-1 !px-2 text-xs" data-testid={`pulse-post-view-${p.post_id}`}>
                        <ExternalLink className="w-3.5 h-3.5" /> Read
                      </Link>
                      <button onClick={() => removePost(p.post_id)} className="fl-btn ghost !py-1 !px-2 text-xs" aria-label="Delete post" data-testid={`pulse-post-delete-${p.post_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
